import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRiskMapData } from '../../hooks/useRiskMapData';
import { RISK_CLASSIFICATION_FREQUENCY_BUCKETS } from '../../utils/riskClassificationConstants';
import './RiskMapView.css';

/**
 * Phase 3: Risk map view and export. Table matches regulatory format:
 * Nr. crt, Section/Compartment, Risk level (color) score, Proposed autocontrol rhythm, Criteria, Mitigation methods.
 */
const RiskMapView = ({ organizationId, structure }) => {
  const { t } = useTranslation('riskClassification');
  const { rows, totalEntities, configuredCount, loading, error, refetch } = useRiskMapData(organizationId, structure);

  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterCriticality, setFilterCriticality] = useState('');
  const [filterExplicitInherited, setFilterExplicitInherited] = useState('');

  const filteredRows = useMemo(() => {
    let list = rows.filter((r) => r.hasExplicitAssignment);
    if (filterEntityType) {
      list = list.filter((r) => r.entityType === filterEntityType);
    }
    if (filterCriticality) {
      list = list.filter((r) => r.riskLevel?.id === filterCriticality);
    }
    if (filterExplicitInherited === 'explicit') {
      list = list.filter((r) => r.hasExplicitAssignment);
    } else if (filterExplicitInherited === 'inherited') {
      list = list.filter((r) => r.inherited);
    }
    return list;
  }, [rows, filterEntityType, filterCriticality, filterExplicitInherited]);

  const uniqueLevels = useMemo(() => {
    const seen = new Set();
    const out = [];
    rows.forEach((r) => {
      if (r.riskLevel && !seen.has(r.riskLevel.id)) {
        seen.add(r.riskLevel.id);
        out.push(r.riskLevel);
      }
    });
    return out.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [rows]);

  const frequencyLabel = (code) => {
    if (!code) return '—';
    const bucket = RISK_CLASSIFICATION_FREQUENCY_BUCKETS.find((f) => f.code === code);
    return bucket ? t(bucket.labelKey) : code;
  };

  const escapeCsv = (val) => {
    if (val == null) return '';
    const s = String(val).replace(/"/g, '""');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s;
  };

  const exportCsv = () => {
    const headers = [
      t('colNrCrt'),
      t('colSectionCompartment'),
      t('colRiskLevelScore'),
      t('colProposedAutocontrolRhythm'),
      t('colCriteriaRiskAndRhythm'),
      t('colMitigationMethods')
    ];
    const lines = [headers.map(escapeCsv).join(',')];
    filteredRows.forEach((row, idx) => {
      const levelText = row.riskLevel
        ? `${row.riskLevel.label} ${row.riskScore != null ? `— ${t('riskScoreLabel')}=${row.riskScore}` : ''}${row.inherited ? ` ${t('inheritedLabel')}` : ''}`
        : '—';
      lines.push(
        [
          idx + 1,
          row.sectionLabel,
          levelText,
          frequencyLabel(row.autocontrolFrequencyCode),
          (row.riskAssessmentRationale || '').replace(/\n/g, '; '),
          (row.additionalMitigationCommitments || '').replace(/\n/g, '; ')
        ]
          .map(escapeCsv)
          .join(',')
      );
    });
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `risk-map-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!organizationId) {
    return (
      <div className="risk-map-view">
        <p className="rcm-empty">{t('noNomenclature')} Select an organization to view the risk map.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="risk-map-view">
        <div className="rcm-loading">{t('loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="risk-map-view">
        <div className="rcm-error">{error}</div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={refetch}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="risk-map-view">
      <div className="rcm-header">
        <h2 className="rcm-title">{t('riskMapTitle')}</h2>
        <span className="rcm-completion">
          {t('riskMapCompletion', { configured: configuredCount, total: totalEntities })}
          {totalEntities > 0 && ` (${Math.round((configuredCount / totalEntities) * 100)}%)`}
        </span>
      </div>

      <div className="rcm-filters">
        <label>
          {t('filterEntityType')}
          <select value={filterEntityType} onChange={(e) => setFilterEntityType(e.target.value)}>
            <option value="">{t('filterAll')}</option>
            <option value="department">{t('entityTypeDepartment')}</option>
            <option value="room">{t('entityTypeRoom')}</option>
            <option value="bed">{t('entityTypeBed')}</option>
            <option value="equipment">{t('entityTypeEquipment')}</option>
          </select>
        </label>
        <label>
          {t('filterCriticality')}
          <select value={filterCriticality} onChange={(e) => setFilterCriticality(e.target.value)}>
            <option value="">{t('filterAll')}</option>
            {uniqueLevels.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('filterExplicitInherited')}
          <select value={filterExplicitInherited} onChange={(e) => setFilterExplicitInherited(e.target.value)}>
            <option value="">{t('filterAll')}</option>
            <option value="explicit">{t('filterExplicitOnly')}</option>
            <option value="inherited">{t('filterInheritedOnly')}</option>
          </select>
        </label>
      </div>

      <div className="rcm-export-wrap">
        <button type="button" className="btn btn-primary btn-sm" onClick={exportCsv}>
          {t('exportCsv')}
        </button>
      </div>

      <div className="rcm-table-wrap">
        {filteredRows.length === 0 ? (
          <div className="rcm-empty">{t('riskMapNoRows')}</div>
        ) : (
          <table className="rcm-table">
            <thead>
              <tr>
                <th className="rcm-nr">{t('colNrCrt')}</th>
                <th className="rcm-section">{t('colSectionCompartment')}</th>
                <th className="rcm-risk-cell">{t('colRiskLevelScore')}</th>
                <th>{t('colProposedAutocontrolRhythm')}</th>
                <th className="rcm-criteria">{t('colCriteriaRiskAndRhythm')}</th>
                <th className="rcm-mitigation">{t('colMitigationMethods')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, idx) => (
                <tr key={`${row.entityType}-${row.entityId}`}>
                  <td className="rcm-nr">{idx + 1}</td>
                  <td className="rcm-section">{row.sectionLabel}</td>
                  <td className="rcm-risk-cell">
                    <div className="rcm-risk-level-row">
                      {row.riskLevel && (
                        <>
                          <span
                            className="rcm-color-swatch"
                            style={{ backgroundColor: row.riskLevel.color || '#6b7280' }}
                            title={row.riskLevel.label}
                          />
                          <span>{row.riskLevel.label}</span>
                        </>
                      )}
                      {row.riskScore != null && (
                        <span className="rcm-risk-score">
                          {t('riskScoreLabel')}={row.riskScore}
                        </span>
                      )}
                      {row.inherited && <span className="rcm-inherited">{t('inheritedLabel')}</span>}
                      {!row.riskLevel && !row.riskScore && '—'}
                    </div>
                  </td>
                  <td>{frequencyLabel(row.autocontrolFrequencyCode)}</td>
                  <td className="rcm-criteria">{row.riskAssessmentRationale || '—'}</td>
                  <td className="rcm-mitigation">{row.additionalMitigationCommitments || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RiskMapView;
